import { LoadingOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { forgotPassword } from '@/controller/auth';
import { toast } from '@/shared/components/UI';
// import logo from '@/shared/img/login/login-banner.png';
import useMessageHook from '@/shared/hooks/useMessageHook';
import { useTranslations } from '@/translations/useTranslations.js';

const ForgotPassword = () => {
    // const { t, i18n } = useTranslation('common');
    const { t } = useTranslations();

    const nav = useNavigate();

    const [loading, setLoading] = useState(false);

    const [isSuccess, setSuccess] = useState(false);
    const [email, setEmail] = useState('');
    const [formData, setFormData] = useState({ email: '' });
    const [errors, setErrors] = useState({});
    const { requiredMsg, emailMsg } = useMessageHook();

    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const type = searchParams.get('type');
        const em = searchParams.get('email');

        if (type !== 'done-reset' || em === '' || em === null) {
            searchParams.delete('type');
            searchParams.delete('email');
            setSearchParams(searchParams);
        } else {
            setEmail(em);
            setSuccess(true);
            searchParams.delete('type');
            searchParams.delete('email');
            setSearchParams(searchParams);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    // Form validation
    const validateForm = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = requiredMsg('email');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = emailMsg('email');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        await onFinish(formData);
    };

    const onFinish = async (v) => {
        setLoading(true);
        try {
            const ret = await forgotPassword(v.email);
            console.log('kemey');
            console.log(ret);
            if (ret.status === 200) {
                setEmail(v.email);
                setSuccess(true);
            } else {
                setLoading(false);
                if (Array.isArray(ret.data?.data?.email)) {
                    setError('email', 'The selected email is invalid.');
                } else {
                    setError('email', ret.data.data.error);
                    toast.error(ret.data.message);
                }
            }
        } catch (e) {}
        console.log(v);
    };

    const setError = (name, error) => {
        setErrors((prev) => ({
            ...prev,
            [name]: error,
        }));
    };

    // Success state - similar to VerificationPage pattern
    if (isSuccess) {
        return (
            <div className="relative flex size-full min-h-screen flex-col bg-white">
                <div className="px-4 sm:px-8 md:px-16 lg:px-32 lg:px-40 xl:px-40 flex flex-1 justify-center py-5 font-sans">
                    <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                        {/* Header Section */}
                        <div className="flex flex-wrap gap-3 p-4">
                            <div>
                                {/* Mail Icon */}
                                <div className="flex  mb-6">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                        <svg
                                            className="w-8 h-8 text-gray-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                    Check your email
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-4">
                                    We've sent password reset instruction to:{' '}
                                    <span className="text-[#111218] font-medium">{email}</span>
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-2">
                                    If you haven't received this email within a few minutes, please check your spam
                                    folder.
                                </p>
                            </div>
                        </div>

                        {/* Action Section */}
                        <div className="px-4 py-6 text-center">
                            <button
                                className="min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-[#111218] text-white text-sm font-bold leading-normal transition-colors duration-200 hover:bg-[#2a2d35] w-full"
                                onClick={() => nav('/')}
                            >
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Form state
    return (
        <div className="relative flex size-full min-h-screen flex-col bg-white">
            <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5 font-sans">
                <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                    {/* Header Section */}
                    <div className="flex flex-wrap justify-between gap-3 p-4">
                        <div>
                            <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                {t('label.forgot_password')}
                            </p>
                            <p className="text-[#5f678c] text-sm font-normal leading-normal mt-2">
                                Enter your email address and we'll send you a link to reset your password.
                            </p>
                        </div>
                    </div>

                    {/* Loading Overlay */}
                    {loading && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
                                <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                                <span className="text-gray-700">Processing...</span>
                            </div>
                        </div>
                    )}

                    {/* Forgot Password Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Email Field */}
                        <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                            <label className="flex flex-col flex-1">
                                <p className="text-gray-900 text-base font-medium pb-2">
                                    Email
                                    <span className="text-red-500 ml-1">*</span>
                                </p>
                                <input
                                    type="email"
                                    className={`w-full h-14 rounded-xl border ${errors.email ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                    value={formData.email}
                                    onChange={(e) => handleFieldChange('email', e.target.value)}
                                    placeholder="Email"
                                    maxLength={50}
                                    disabled={loading}
                                />
                                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                            </label>
                        </div>

                        {/* Submit Button */}
                        <div className="p-4 flex justify-center">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                            >
                                <span className="overflow-hidden">
                                    {loading ? 'Processing...' : t('label.reset_password')}
                                </span>
                            </button>
                        </div>

                        {/* Back to Login Link */}
                        <div className="px-4 pb-4">
                            <div className="text-center">
                                <p className="text-sm text-gray-600">
                                    Back to{' '}
                                    <button
                                        type="button"
                                        onClick={() => nav('/login')}
                                        className="text-blue-600 font-medium hover:text-blue-700 transition-colors duration-200 hover:underline"
                                    >
                                        Login
                                    </button>
                                </p>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
